@if (form) {
  <div class="standard-dialog">
    <div class="header" mat-dialog-title>
      <div class="title-section">
        <h2>{{ (dialogData.type == 'edit' ? 'Templates.EditButton' : 'Templates.CreateButton') | translate }}</h2>
        <p class="subtitle">{{ 'Templates.Subtitle' | translate }}</p>
      </div>
      <button mat-icon-button (click)="dialogRef.close()" class="close-btn">
        <mat-icon>close</mat-icon>
      </button>
    </div>
    <mat-dialog-content class="form-content">
      <form [formGroup]="form" class="flex flex-col gap-3">
        <!-- Template Name -->
        <div class="w-100">
          <mat-label class="form-label mb-1">{{ 'Templates.Name' | translate }}</mat-label>
          <mat-form-field subscriptSizing="dynamic" appearance="fill" class="soft-input w-100">
            <input matInput [formControl]="form.controls['name']" [placeholder]="'Templates.EnterName' | translate" />
          </mat-form-field>
        </div>

        <!-- AI Assistant Section -->
        <div class="ai-section" [style.opacity]="canUseAI ? 1 : 0.6">
          <div class="ai-section-header" (click)="canUseAI && toggleAiSection()" [matTooltip]="!canUseAI ? ('GenerateContent.NoAiAccessTooltip' | translate) : ''" [style.cursor]="canUseAI ? 'pointer' : 'not-allowed'">
            <div class="flex items-center gap-2">
              <mat-icon class="ai-sparkle-icon">auto_awesome</mat-icon>
              <h3 class="ai-section-title">{{ 'Templates.AI.Toggle' | translate }}</h3>
            </div>
            <button mat-icon-button class="toggle-btn" type="button" [disabled]="!canUseAI">
              <mat-icon>{{ aiSectionExpanded() ? 'expand_less' : 'expand_more' }}</mat-icon>
            </button>
          </div>

          @if (canUseAI && aiSectionExpanded()) {
            <div class="ai-section-content">
              <p class="ai-hint">{{ 'Templates.AI.Hint' | translate }}</p>

              <div class="w-100 mb-3">
                <mat-label class="form-label mb-1">{{ 'Templates.AI.Example' | translate }}</mat-label>
                <mat-form-field subscriptSizing="dynamic" appearance="fill" class="soft-input w-100">
                  <textarea
                    matInput
                    rows="3"
                    [placeholder]="'Templates.AI.ExamplePlaceholder' | translate"
                    [value]="aiExampleText()"
                    (input)="aiExampleText.set($any($event.target).value)"
                  ></textarea>
                </mat-form-field>
              </div>

              <div class="w-100 mb-3">
                <mat-label class="form-label mb-1">{{ 'Templates.AI.Instructions' | translate }}</mat-label>
                <mat-form-field subscriptSizing="dynamic" appearance="fill" class="soft-input w-100">
                  <textarea
                    matInput
                    rows="2"
                    [placeholder]="'Templates.AI.InstructionsPlaceholder' | translate"
                    [value]="aiInstructions()"
                    (input)="aiInstructions.set($any($event.target).value)"
                  ></textarea>
                </mat-form-field>
              </div>

              <!-- Attachments & Generate Actions -->
              <input type="file" #fileInput hidden multiple (change)="onFileSelected($event)" accept=".pdf,.doc,.docx,.txt,image/*" />

              <!-- Attach button + file chips in one horizontal flow -->
              <div class="flex flex-wrap items-center gap-2 mb-3">
                <button type="button" mat-icon-button class="attach-icon-btn" (click)="fileInput.click()" [matTooltip]="'Templates.AI.AttachFile' | translate">
                  <mat-icon>attach_file</mat-icon>
                </button>
                @for (file of attachedFiles(); track file.id) {
                  <div class="file-chip">
                    <mat-icon class="file-icon">description</mat-icon>
                    <span class="file-name" [title]="file.name">{{ file.name }}</span>
                    @if (file.uploading) {
                      <mat-spinner diameter="14" class="file-action-icon"></mat-spinner>
                    } @else {
                      <button type="button" mat-icon-button class="file-remove-btn" (click)="removeAttachment(file.id)">
                        <mat-icon class="file-action-icon">close</mat-icon>
                      </button>
                    }
                  </div>
                }
              </div>

              <!-- Generate button — primary CTA -->
              <div class="flex justify-end">
                <button
                  type="button"
                  mat-flat-button
                  color="primary"
                  class="generate-btn"
                  [disabled]="aiGenerating() || (!aiExampleText() && !aiInstructions() && attachedFiles().length === 0)"
                  (click)="generateWithAI()"
                >
                  <div class="flex items-center gap-2">
                    @if (aiGenerating()) {
                      <mat-spinner diameter="18"></mat-spinner>
                    } @else {
                      <mat-icon>auto_awesome</mat-icon>
                    }
                    <span>{{ 'Templates.AI.Generate' | translate }}</span>
                  </div>
                </button>
              </div>
            </div>
          }
        </div>

        <!-- Template Content Editor -->
        <div class="w-100">
          <mat-label class="form-label mb-1">{{ 'Templates.Content' | translate }}</mat-label>
          <div id="models-toolbar">
            <span class="ql-formats">
              <button mat-icon-button (click)="undo()" [disabled]="!canUndo" matTooltip="Undo">
                <mat-icon>undo</mat-icon>
              </button>
              <button mat-icon-button (click)="redo()" [disabled]="!canRedo" matTooltip="Redo">
                <mat-icon>redo</mat-icon>
              </button>
            </span>
            <span class="divider"></span>
            <span class="ql-formats">
              <button mat-button [matMenuTriggerFor]="headerMenu" class="header-dropdown-btn" matTooltip="Text style">
                {{ currentHeaderLabel | translate }}
                <mat-icon>arrow_drop_down</mat-icon>
              </button>
              <mat-menu #headerMenu="matMenu">
                @for (option of headerOptions; track option.id) {
                  <button mat-menu-item (click)="applyHeader(option.id)">
                    <mat-icon>{{ option.icon }}</mat-icon>
                    <span>{{ option.text | translate }}</span>
                  </button>
                }
                <mat-divider></mat-divider>
                <button mat-menu-item (click)="clearFormatting()">
                  <mat-icon>format_clear</mat-icon>
                  <span>{{ 'Clear Formatting' | translate }}</span>
                </button>
              </mat-menu>
            </span>
            <span class="divider"></span>
            <span class="ql-formats">
              <button class="ql-bold" mat-icon-button matTooltip="Bold"><mat-icon>format_bold</mat-icon></button>
              <button class="ql-italic" mat-icon-button matTooltip="Italic"><mat-icon>format_italic</mat-icon></button>
              <button class="ql-underline" mat-icon-button matTooltip="Underline"><mat-icon>format_underlined</mat-icon></button>
            </span>
            <span class="divider"></span>
            <span class="ql-formats">
              <button class="ql-list" value="ordered" mat-icon-button matTooltip="Ordered list"><mat-icon>format_list_numbered</mat-icon></button>
              <button class="ql-list" value="bullet" mat-icon-button matTooltip="Bullet list"><mat-icon>format_list_bulleted</mat-icon></button>
            </span>
            <span class="divider"></span>
            <span class="ql-formats">
              <button class="ql-align" value="" mat-icon-button matTooltip="Align Left"><mat-icon>format_align_left</mat-icon></button>
              <button class="ql-align" value="center" mat-icon-button matTooltip="Align Center"><mat-icon>format_align_center</mat-icon></button>
              <button class="ql-align" value="right" mat-icon-button matTooltip="Align Right"><mat-icon>format_align_right</mat-icon></button>
              <button class="ql-align" value="justify" mat-icon-button matTooltip="Justify"><mat-icon>format_align_justify</mat-icon></button>
            </span>
          </div>
          <quill-editor
            #editor
            [formControl]="form.controls['data']"
            [modules]="quillModules"
            class="full-width"
            placeholder="{{ 'Templates.EnterContent' | translate }}"
            (onContentChanged)="onContentChanged($event)"
            (onSelectionChanged)="onSelectionChanged($event)"
          ></quill-editor>
        </div>
      </form>
    </mat-dialog-content>
    <mat-dialog-actions class="footer">
      <div class="left-actions"></div>
      <div class="right-actions">
        <button mat-button (click)="dialogRef.close()">{{ 'Global.Cancel' | translate }}</button>
        <button mat-flat-button color="primary" (click)="submit()" [disabled]="loading || form.invalid || dataIsEmpty()">
          <div class="flex items-center gap-2">
            @if (loading) {
              <mat-spinner diameter="18"></mat-spinner>
            }
            <span>{{ 'Templates.SaveButton' | translate }}</span>
          </div>
        </button>
      </div>
    </mat-dialog-actions>
  </div>
}
